import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { Alert, Button, Modal, Progress, Space, Tooltip } from "antd";
import { SyncOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Folder, RenewalPointer } from "../../redux/interfaces/folder.types.ts";
import { renewalButtonState } from "../../utils/folders/canRenewFolder.ts";
import {
  RenewalError,
  RenewalResult,
  createFolderRenewal,
} from "../../api/renewals.ts";
import { useSingleFlight } from "../../hooks/useSingleFlight.ts";
import {
  pendingRequirementLines,
  reasonLabel,
  skippedSubfolderLines,
} from "../../utils/folders/renewalSummaryLines.ts";
import {
  renewalProgressPercent,
  renewalStageFor,
} from "../../utils/folders/renewalProgress.ts";

interface Props {
  folder: Folder;
  isAdmin: boolean;
}

/** Segundos que el resumen queda en pantalla antes de abrir el folio nuevo. */
const REDIRECT_DELAY_SECONDS = 10;

/**
 * Cuántos pendientes se enumeran antes de resumir el resto. Una renovación con
 * varios participantes trae decenas, y una lista de treinta líneas no se lee:
 * el total es la señal, el detalle de los primeros es por dónde empezar.
 */
const PENDING_PREVIEW_LIMIT = 5;

/**
 * MB-379 — botón que crea el expediente de renovación.
 *
 * Lo que la pantalla promete importa tanto como lo que hace: esta acción NO
 * entrega un contrato, entrega un expediente completo en revisión. Por eso el
 * botón no dice "Renovar" a secas y la confirmación enumera en castellano llano
 * lo que se copia, lo que no, y sobre todo que nada llega validado. Un CX que
 * lee "renovar" espera un contrato y encuentra una bandeja de pendientes.
 *
 * El cuerpo de la petición va vacío a propósito: espejar el obligado solidario
 * del expediente anterior es el caso normal, y añadirlo o quitarlo se decide
 * después, editando el expediente nuevo. Elegirlo aquí obligaría a decidir
 * sobre el año que viene antes de haber mirado el expediente.
 */
export const RenewFolder: React.FC<Props> = ({ folder, isAdmin }) => {
  const navigate = useNavigate();
  // El folio de la RUTA, que cambia en cuanto se navega; `folder` es el que ya
  // cargó y se queda en el anterior mientras llega el siguiente.
  const { id: routeFolderId } = useParams();
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState<RenewalError | null>(null);
  const [result, setResult] = useState<RenewalResult | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [secondsToRedirect, setSecondsToRedirect] = useState(
    REDIRECT_DELAY_SECONDS,
  );
  const { isPending, run } = useSingleFlight();
  // Enlaza el botón deshabilitado con su motivo para lectores de pantalla: el
  // tooltip sólo aparece al pasar el ratón.
  const blockReasonId = useId();

  // El 409 es la palabra definitiva sobre "ya existe una renovación": el
  // backend hace la consulta inversa que el cliente no puede hacer, así que
  // cuando llega, en esta visita ya no se ofrece crear. Reintentar sólo
  // produciría el mismo 409, y la metadata que oculta el botón puede estar
  // desfasada en folios renovados antes de que ese puntero existiera. Se
  // guarda el PUNTERO, no un "sí/no": cerrado el modal, el botón se convierte
  // en el mismo enlace "Ver la renovación" que pinta la metadata.
  const [foundRenewal, setFoundRenewal] = useState<RenewalPointer | null>(null);

  // El folio de origen de la renovación en vuelo, mientras la haya. La
  // petición es de un solo vuelo por pantalla y la pantalla sobrevive al cambio
  // de folio: si se pidió en otra visita, aquí "Crear" no haría nada y el modal
  // enseñaría el progreso ajeno bajo el título de este folio, sin poder
  // cerrarse. `inFlightElsewhere` es ese folio cuando la visita ya no es la que
  // la pidió; el botón lo explica en vez de abrir nada.
  const inFlightOrigin = useRef<number | string | null>(null);
  const [inFlightElsewhere, setInFlightElsewhere] = useState<
    number | string | null
  >(null);

  // El contador corre sólo mientras hay petición en vuelo; al terminar se
  // reinicia para que un segundo intento no herede el tiempo del primero.
  useEffect(() => {
    if (!isPending) {
      setElapsedSeconds(0);
      return;
    }

    const startedAt = Date.now();
    const interval = window.setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);

    return () => window.clearInterval(interval);
  }, [isPending]);

  // Irse a otro expediente ocurre una sola vez por visita al folio de origen.
  // Hace falta recordarlo porque `navigate` a `/folders/<otro id>` deja este
  // componente MONTADO —la ruta sólo cambia de parámetro—, así que sin el
  // guardia el efecto de la cuenta atrás volvería a navegar en cada render con
  // el contador ya a cero. El efecto de cambio de folio lo vacía: si se vuelve
  // al MISMO origen y se renueva otra vez, el guardia de la visita anterior
  // dejaba el resumen atascado en cero y "Abrir" sin hacer nada.
  const redirectedFrom = useRef<string | null>(null);

  // Cuenta las visitas que terminan: cambiar de folio y desmontar suman una.
  // Comparar ids no basta: de A a B y de vuelta a A el id es el mismo, pero el
  // modal ya se cerró, y el resultado acabaría escrito con el modal cerrado y
  // una cuenta atrás invisible navegando sola al folio nuevo.
  const visit = useRef(0);

  const goToFolder = useCallback(
    (folderId: string) => {
      if (redirectedFrom.current === folder.id) return;
      redirectedFrom.current = folder.id;

      // Apagar el modal ANTES de navegar. Como el componente sobrevive al
      // cambio de ruta, nadie lo cerraría por nosotros: el expediente nuevo
      // cargaría por detrás, tapado por el resumen del anterior, con la cuenta
      // atrás corriendo hasta cero y el botón ya convertido en un no-op.
      // Soltar `result` es además lo que detiene el temporizador: sin resumen,
      // el efecto de la cuenta atrás no tiene nada que contar.
      setIsOpen(false);
      setResult(null);
      setError(null);
      setSecondsToRedirect(REDIRECT_DELAY_SECONDS);

      navigate(`/folders/${folderId}`);
    },
    [folder.id, navigate],
  );

  // Cuenta atrás hacia el folio nuevo. No se navega de inmediato porque el
  // recuento de pendientes y la lista de lo que no se pudo traer existen SÓLO
  // en esta respuesta: al cambiar de pantalla se pierden, y con ellos la
  // diferencia entre "listo" y "listo salvo estas tres cosas".
  useEffect(() => {
    if (!result) return;
    if (secondsToRedirect <= 0) {
      goToFolder(result.id);
      return;
    }

    const timeout = window.setTimeout(
      () => setSecondsToRedirect((seconds) => seconds - 1),
      1000,
    );
    return () => window.clearTimeout(timeout);
  }, [result, secondsToRedirect, goToFolder]);

  // El componente sobrevive al cambio de expediente, así que el estado del
  // folio anterior se quedaría pegado al siguiente: el 409 de uno dejaría el
  // botón del otro muerto, y el resumen de uno se abriría sobre el otro. Al
  // cambiar de folio se vuelve a cero, guardia de navegación incluido.
  //
  // Cambiar de folio es cambiar la RUTA, no esperar a que llegue el expediente
  // nuevo: `CardView` sigue pintando el anterior mientras carga el siguiente, y
  // una renovación que terminaba en ese hueco abría su resumen y su cuenta
  // atrás —aunque la carga tardara o fallara— sobre un folio que ya se dejó.
  useEffect(() => {
    redirectedFrom.current = null;
    setIsOpen(false);
    setError(null);
    setResult(null);
    setSecondsToRedirect(REDIRECT_DELAY_SECONDS);
    setFoundRenewal(null);
    // Una petición que sigue en vuelo ya no es de esta visita.
    setInFlightElsewhere(inFlightOrigin.current);
    // La limpieza corre al cambiar de folio y al desmontar: las dos son irse.
    return () => {
      visit.current += 1;
    };
  }, [folder.id, routeFolderId]);

  if (!isAdmin) return null;

  const buttonState = renewalButtonState(folder, {
    foundRenewal,
    inFlightFolio: inFlightElsewhere,
  });
  if (buttonState.kind === "hidden") return null;

  // Ya renovado: el botón de crear NO aparece. Pero esconderlo y no decir nada
  // deja al CX buscando a mano el folio que ya existe, así que se ofrece el
  // camino en su lugar. Es navegación, no la acción: no crea nada.
  //
  // No renovable: el botón se sigue viendo, deshabilitado, y el tooltip dice
  // por qué. Esconderlo dejaba al CX sin saber si la acción existe o qué le
  // falta al expediente. El motivo es el mismo —y en el mismo orden— que daría
  // el backend, y este botón no abre el modal ni llama a nadie.
  //
  // El `span` no es decorativo: un `<button disabled>` no garantiza eventos de
  // ratón en todos los navegadores, y antd 5 ya no envuelve los hijos
  // deshabilitados del Tooltip (lo hacía la v4). Con `pointer-events: none` en
  // el botón, el ratón lo atraviesa y es el `span` quien abre el tooltip.
  //
  // El `span` también es enfocable (`tabIndex={0}`) y el tooltip se abre con el
  // foco: un botón deshabilitado sale del orden de tabulación, así que sin esto
  // quien navega con teclado nunca llegaría al motivo. El `aria-describedby` del
  // `span` no se toca: rc-tooltip lo sobrescribe con el id de su propio popup,
  // que es justo el motivo.
  //
  // El modal se sigue pintando en los tres casos: un 409 convierte el botón en
  // el enlace con el modal todavía abierto, y su aviso tiene que poder leerse.
  const trigger =
    buttonState.kind === "renewed" ? (
      <Button
        type="link"
        className="px-0"
        icon={<SyncOutlined />}
        onClick={() => navigate(`/folders/${buttonState.id}`)}
      >
        {buttonState.folderNumber
          ? `Ver la renovación #${buttonState.folderNumber}`
          : "Ver la renovación"}
      </Button>
    ) : buttonState.kind === "blocked" ? (
      <Tooltip title={buttonState.reason} trigger={["hover", "focus"]}>
        <span
          data-testid="renewal-blocked"
          tabIndex={0}
          // `inline-flex` y no `inline-block`: el bloque en línea reserva el hueco
          // de los descendentes bajo el botón y lo desalinea del resto de la fila.
          style={{ cursor: "not-allowed", display: "inline-flex" }}
        >
          <Button
            icon={<SyncOutlined />}
            disabled
            aria-describedby={blockReasonId}
            style={{ pointerEvents: "none" }}
          >
            Crear renovación
          </Button>
          <span id={blockReasonId} className="sr-only">
            {buttonState.reason}
          </span>
        </span>
      </Tooltip>
    ) : (
      <Tooltip title="Crea un expediente nuevo copiando este. No genera el contrato.">
        <Button
          icon={<SyncOutlined />}
          onClick={() => {
            setError(null);
            setIsOpen(true);
          }}
        >
          Crear renovación
        </Button>
      </Tooltip>
    );

  const handleRenew = () =>
    run(async () => {
      // La petición tarda y la navegación sigue viva: si al volver ya no es la
      // misma visita (otro folio, o vuelta al mismo, o pantalla desmontada), el
      // resultado no es de esta pantalla. Se avisa con el toast de la raíz, que
      // sobrevive al desmontaje, y no se toca el estado.
      const originId = folder.id;
      const originNumber = folder.folder_number;
      const startedVisit = visit.current;
      const leftOrigin = () => visit.current !== startedVisit;

      setError(null);
      inFlightOrigin.current = originNumber;
      try {
        const renewal = await createFolderRenewal({ folderId: originId });
        if (leftOrigin()) {
          toast.success(
            `Se creó el expediente de renovación #${renewal.folderNumber} a partir del #${originNumber}.`,
          );
          return;
        }
        setSecondsToRedirect(REDIRECT_DELAY_SECONDS);
        setResult(renewal);
      } catch (caught) {
        // El error se queda en el modal, no en un toast que se desvanece: es la
        // única pista de por qué no hay expediente nuevo.
        const renewalError =
          caught instanceof RenewalError
            ? caught
            : new RenewalError("No se pudo crear el expediente de renovación.");
        if (leftOrigin()) {
          toast.error(
            `No se creó la renovación del expediente #${originNumber}. ${renewalError.message}`,
          );
          return;
        }
        setError(renewalError);
        if (renewalError.kind === "already_exists") {
          setFoundRenewal({
            id: renewalError.existingFolderId,
            folder_number: renewalError.existingFolderNumber,
          });
        }
      } finally {
        inFlightOrigin.current = null;
        setInFlightElsewhere(null);
      }
    });

  const percent = result ? 100 : renewalProgressPercent(elapsedSeconds);

  const closeModal = () => {
    // Si el expediente ya se creó, cerrar el resumen equivale a darlo por leído:
    // se abre el folio nuevo en ese momento. Lo contrario sería dejar la cuenta
    // atrás corriendo por detrás y arrastrar a quien decidió quedarse.
    if (result) {
      goToFolder(result.id);
      return;
    }
    setIsOpen(false);
    setError(null);
  };

  const existingFolderId = error?.existingFolderId;

  return (
    <>
      {trigger}

      <Modal
        open={isOpen}
        title={`Crear expediente de renovación del #${folder.folder_number}`}
        onCancel={closeModal}
        // Mientras se crea, el modal se vuelve inamovible: cerrarlo no cancela
        // nada en el servidor, sólo esconde el resultado.
        closable={!isPending}
        maskClosable={!isPending}
        keyboard={!isPending}
        width={640}
        // Al cerrarse, el contenido se destruye. Sin esto antd congela los
        // hijos mientras el modal está invisible (rc-dialog deja de
        // actualizarlos), así que el resumen del expediente anterior seguiría
        // en el DOM y reaparecería tal cual al abrirlo para el siguiente.
        destroyOnHidden
        footer={
          // Durante la ejecución no hay botones: nada que pulsar es la forma más
          // clara de decir "no vuelvas a pulsar".
          isPending ? null : result ? (
            <Button type="primary" onClick={() => goToFolder(result.id)}>
              {`Abrir el expediente #${result.folderNumber}`}
            </Button>
          ) : (
            <Space>
              <Button onClick={closeModal}>Cancelar</Button>
              {existingFolderId ? (
                <Button
                  type="primary"
                  onClick={() => goToFolder(existingFolderId)}
                >
                  {error?.existingFolderNumber
                    ? `Abrir el expediente #${error.existingFolderNumber}`
                    : "Abrir la renovación existente"}
                </Button>
              ) : (
                <Button
                  type="primary"
                  disabled={foundRenewal !== null}
                  onClick={handleRenew}
                >
                  Crear expediente de renovación
                </Button>
              )}
            </Space>
          )
        }
      >
        {isPending ? (
          <div data-testid="renewal-progress" className="py-2">
            <Progress percent={percent} status="active" showInfo={false} />
            <p className="mt-3 mb-1 font-semibold">
              {renewalStageFor(elapsedSeconds)}
            </p>
            <p className="text-sm text-gray-500">
              {elapsedSeconds} {elapsedSeconds === 1 ? "segundo" : "segundos"}{" "}
              transcurridos. Suele tardar alrededor de un minuto.
            </p>
            <p className="text-sm text-gray-500">
              No cierres esta ventana: el expediente se está creando y al
              terminar verás el resumen.
            </p>
          </div>
        ) : result ? (
          <div
            data-testid="renewal-result"
            className="flex flex-col gap-3 py-1"
          >
            <Alert
              type="success"
              showIcon
              message={`Se creó el expediente de renovación #${result.folderNumber}`}
              description="Queda EN REVISIÓN. Esto es lo que falta para poder generar el contrato:"
            />
            <ul className="list-disc pl-5">
              <li>
                Capturar la <strong>renta</strong> y la{" "}
                <strong>vigencia</strong>: llegaron vacías.
              </li>
              <li>
                Revisar los requisitos: ninguno llega validado.
                {result.filesCopied > 0 &&
                  ` Se copiaron ${result.filesCopied} ${
                    result.filesCopied === 1 ? "archivo" : "archivos"
                  } del expediente anterior.`}
              </li>
            </ul>

            {result.requirementsPendingUpload.length > 0 && (
              <div>
                <p className="font-semibold mb-1">
                  {`${result.requirementsPendingUpload.length} ${
                    result.requirementsPendingUpload.length === 1
                      ? "requisito quedó pendiente de carga"
                      : "requisitos quedaron pendientes de carga"
                  }`}
                </p>
                <ul className="list-disc pl-5">
                  {pendingRequirementLines(result.requirementsPendingUpload)
                    .slice(0, PENDING_PREVIEW_LIMIT)
                    .map((line) => (
                      <li key={line.key}>
                        {line.label}
                        {line.reason && ` — ${reasonLabel(line.reason)}`}
                      </li>
                    ))}
                  {result.requirementsPendingUpload.length >
                    PENDING_PREVIEW_LIMIT && (
                    <li>
                      {`y ${result.requirementsPendingUpload.length - PENDING_PREVIEW_LIMIT} más, marcados en el expediente nuevo.`}
                    </li>
                  )}
                </ul>
              </div>
            )}

            {result.skippedSubfolders.length > 0 && (
              <div>
                <p className="font-semibold mb-1">
                  No se trajeron estos participantes
                </p>
                <ul className="list-disc pl-5">
                  {skippedSubfolderLines(result.skippedSubfolders).map(
                    (line) => (
                      <li key={line.key}>
                        {line.label}
                        {line.reason && ` — ${reasonLabel(line.reason)}`}
                      </li>
                    ),
                  )}
                </ul>
                <p className="text-sm text-gray-500 mt-1">
                  Si siguen aplicando, hay que darlos de alta a mano en el
                  expediente nuevo.
                </p>
              </div>
            )}
            <p
              data-testid="renewal-countdown"
              className="text-sm text-gray-500"
            >
              {`Se abrirá el expediente #${result.folderNumber} en ${secondsToRedirect} s.`}
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-3 py-1">
            <p>
              Se crea un <strong>expediente nuevo</strong> a partir del #
              {folder.folder_number}. <strong>No se genera el contrato</strong>:
              lo que obtienes es un expediente completo en revisión.
            </p>

            <div>
              <p className="font-semibold mb-1">
                Se copia del expediente actual
              </p>
              <ul className="list-disc pl-5">
                <li>
                  Los participantes: arrendador, inquilino y, si aplica,
                  obligado solidario, con sus representantes.
                </li>
                <li>
                  Sus datos de formulario y los documentos que ya habían
                  entregado. Los archivos se copian al expediente nuevo, no se
                  comparten.
                </li>
                <li>Los datos del inmueble.</li>
                <li>
                  El clausulado del contrato anterior se hereda después, cuando
                  se genere el contrato nuevo.
                </li>
              </ul>
            </div>

            <div>
              <p className="font-semibold mb-1">No se copia</p>
              <ul className="list-disc pl-5">
                <li>
                  No se vuelve a investigar: el expediente nuevo{" "}
                  <strong>no genera multireporte</strong>.
                </li>
                <li>
                  La renta y la vigencia llegan <strong>vacías</strong>. Las
                  captura quien revise, editando el expediente.
                </li>
                <li>
                  La póliza de renta no se hereda: se contrata al renovar, si
                  aplica.
                </li>
              </ul>
            </div>

            <Alert
              type="warning"
              showIcon
              message="Hay que revisar todo de nuevo"
              description={
                <span>
                  Ningún requisito se copia como <strong>validado</strong>:
                  todos llegan <strong>por revisar</strong>, y los que no se
                  pudieron traer quedan <strong>pendientes de carga</strong>.
                  Revísalos uno por uno y captura la renta y la vigencia antes
                  de generar el contrato.
                </span>
              }
            />

            {error && (
              <Alert
                type="error"
                showIcon
                message="No se creó el expediente de renovación"
                description={error.message}
              />
            )}
          </div>
        )}
      </Modal>
    </>
  );
};
